import React, { useEffect, useRef, useState } from 'react';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import { ScheduleIndexResponse } from '@/dashboard/services/dashboardApi';
import { useTablet } from '@/hooks/responsiveHooks';
import { reduceSchedulesResponseToLabels } from '@/dashboard/utils/schoolYearSelector.utils';

interface SchoolYearSelectorProps {
  schedules: ScheduleIndexResponse;
  schoolYear: number;
  label?: string;
  width?: [number, number];
  onChange: (year: number, count: number) => void;
  allYearRequired: boolean;
  title?: string;
  disabled?: boolean;
  updateFromProp?: boolean;
  stretch?: boolean;
}

function SchoolYearSelector({
  schedules,
  schoolYear,
  onChange,
  label = 'Select School Year',
  width = [250, 270],
  allYearRequired,
  title = 'Select a school year',
  disabled = false,
  updateFromProp = false,
  stretch = false,
}: SchoolYearSelectorProps) {
  const isTablet = useTablet();
  const onChangeCounter = useRef(0);
  const range = reduceSchedulesResponseToLabels(schedules);
  const [year, setYear] = useState(schoolYear);

  if (range.length <= 0) {
    throw new Error('Unable to build range for school year selector.');
  }

  useEffect(() => {
    if (updateFromProp) {
      setYear(schoolYear);
    }
  }, [schoolYear, updateFromProp]);

  useEffect(() => {
    const timer: ReturnType<typeof setTimeout> = setTimeout(() => {
      onChange(year, ++onChangeCounter.current);
    }, 200);
    return () => {
      clearTimeout(timer);
    };
  }, [year]);

  return (
    <FormControl
      variant="outlined"
      size="small"
      sx={{ minWidth: isTablet ? width[0] : width[1], mb: 0, flex: stretch ? 1 : 0 }}
    >
      <InputLabel
        id="school-year-drop-down"
        htmlFor="school-year-select"
        sx={{ color: 'custom.darkGrey' }}
      >
        {label}
      </InputLabel>
      <Select
        data-testid="school-year-select"
        name="school-year-select-menu"
        value={year}
        labelId="school-year-drop-down"
        variant="outlined"
        label={label}
        title={title}
        onChange={(event) => setYear(Number(event.target.value))}
        disabled={disabled}
        inputProps={{ id: 'school-year-select' }}
      >
        {allYearRequired ? <MenuItem value={0}>All Years</MenuItem> : null}
        {range.map((item) => (
          <MenuItem key={item.year} value={item.year}>
            {item.range}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export default SchoolYearSelector;
